import { TrendingUp, DollarSign, ShieldCheck, Wrench } from "lucide-react";
import { BENEFITS_IMAGE } from "../lib/constants";
import useReveal from "../hooks/useReveal";

const BENEFITS = [
  {
    icon: TrendingUp,
    title: "Greater Energy Production",
    text: "Restore maximum output by removing dust, pollen, and debris that silently steal efficiency.",
  },
  {
    icon: DollarSign,
    title: "Lower Utility Bills",
    text: "Cleaner panels generate more power — translating directly into meaningful monthly savings.",
  },
  {
    icon: ShieldCheck,
    title: "Protect Your Investment",
    text: "Routine professional care extends the lifespan of your system and preserves long-term value.",
  },
];

export default function Benefits() {
  const head = useReveal();
  const img = useReveal();

  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text column */}
          <div
            ref={head.ref}
            className={`fade-in-up ${head.visible ? "is-visible" : ""} lg:col-span-7`}
          >
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
              Why Clean Your Panels
            </span>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] text-[#0A192F] tracking-tight">
              Cleaner panels. Stronger output.{" "}
              <span className="text-[#00B4D8]">Real savings.</span>
            </h2>
            <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Professional solar panel cleaning significantly increases energy
              production, reduces your utility bills, and safeguards the
              long-term performance of your solar investment. It is the single
              most effective way to preserve the efficiency and value of your
              system.
            </p>

            <div className="mt-8 grid sm:grid-cols-3 gap-5">
              {BENEFITS.map((b, i) => (
                <div
                  key={i}
                  data-testid={`benefit-card-${i}`}
                  className="p-5 rounded-2xl border border-slate-100 bg-[#F8FAFC] hover:border-[#00B4D8]/30 hover:bg-white hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#00B4D8]/10 flex items-center justify-center">
                    <b.icon className="w-5 h-5 text-[#00B4D8]" />
                  </div>
                  <p className="mt-3 font-heading font-semibold text-[#0A192F] text-base">
                    {b.title}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                    {b.text}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-9 inline-flex items-center gap-3 bg-[#0A192F] text-white px-5 py-3 rounded-full">
              <Wrench className="w-4 h-4 text-[#00B4D8]" />
              <span className="font-heading font-semibold text-sm sm:text-base">
                Over <span className="text-[#00B4D8]">5 years</span> of
                experience as certified solar technicians
              </span>
            </div>
          </div>

          {/* Image column */}
          <div
            ref={img.ref}
            className={`fade-in-up ${img.visible ? "is-visible" : ""} lg:col-span-5`}
          >
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#00B4D8]/30 to-[#0A192F]/20 rounded-3xl blur-2xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-[0_12px_40px_rgb(10,25,47,0.12)]">
                <div className="aspect-[4/5]">
                  <img
                    src={BENEFITS_IMAGE}
                    alt="Professional solar panel installation"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-slate-100">
                  <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#00B4D8]">
                    Tampa Bay · Florida
                  </p>
                  <p className="mt-1 font-heading font-semibold text-[#0A192F]">
                    Trusted by homeowners & businesses
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
